import axios from "axios";

const baseURL = process.env.BARRAMUNDI;

export default {
    /**
     * @param int start - unix timestamp
     * @param int end - unix timestamp
     */
    async getBoardingData(start, end) {
        let res = await axios.get(`${baseURL}schedule/boarding?start=${start}&end=${end}`); // prettier-ignore
        let response = res.data.response;

        if (response && response.resources) {
            return response.resources;
        } else {
            return [];
        }
    },
    async getSettings() {
        let res = await axios.get(baseURL + "schedule/boarding/setting");
        if (res.data.response) {
            return res.data.response;
        } else {
            return null;
        }
    },
    async getRoomList() {
        let res = await axios.get(baseURL + "schedule/boarding/room-list");

        if (res.data && res.data.response) {
            return res.data.response.message || [];
        }

        return [];
    },
    /**
     * @param int start - unix timestamp
     * @param int end - unix timestamp
     * @param string roomId
     */
    async checkRoomAvailability(start, end, roomId) {
        let res = await axios.get(
            baseURL + "schedule/boarding/room-availability",
            {
                params: {
                    start,
                    end,
                    room_id: roomId
                }
            }
        );

        if (
            res.data &&
            res.data.response &&
            res.data.response.message &&
            res.data.response.message.error === 0
        ) {
            return true;
        } else {
            return false;
        }
    },

    /**
     * type    1 = confirmation, 0 = reminder
     * option  1 = email, 0 = sms
     * start   unix timestamp
     * end     unix timestamp
     */
    async getContentMessage({ type, option, clientId, patientId, start, end, note }) {
        let res = await axios.post(
            baseURL + "schedule/boarding/content-message",
            {
                type,
                option,
                client_id: clientId,
                patient_id: patientId,
                start,
                end,
                note
            }
        );

        if (res.data.response && res.data.response.message) {
            if (option === 1) {
                return res.data.response.message.message;
            } else {
                return res.data.response.message;
            }
        } else {
            return "Error: Unable to generate message";
        }
    },
    async saveReservation(data) {
        let res = await axios.post(baseURL + "schedule/boarding", data);

        if (
            res.data.response &&
            res.data.response.code === 200 &&
            res.data.response.message.reservation
        ) {
            return true;
        } else {
            throw new Error(res.data?.response?.message?.message);
        }
    },
    async updateReservation(boardingId, data) {
        let res = await axios.put(
            baseURL + "schedule/boarding/" + boardingId,
            data
        );

        if (
            res.data.response &&
            res.data.response.code === 200 &&
            res.data.response.message.reservation
        ) {
            return true;
        } else {
            throw new Error(res.data.response.message.message);
        }
    },
    async getBoardingById(id) {
        let res = await axios.get(baseURL + "schedule/boarding/" + id);
        let response = res.data.response;

        if (response && response.message && response.message.code === 200) {
            return response.resource;
        } else {
            return null;
        }
    },
    async saveCheckIn(data) {
        let res = await axios.post(
            baseURL + "schedule/boarding/check-in-reservation",
            data
        );

        return res.data;
    },
    async getRoomDetails(roomId) {
        let res = await axios.get(baseURL + "account/rooms/" + roomId);
        let response = res.data.response;
        return response.resources || null;
    },
    async undoCheckIn(id) {
        let url = `${baseURL}schedule/boarding/undo-check-in-reservation`;
        let res = await axios.post(url, { boarding_id: id });

        if (res.data.response && res.data.response.code === 200) {
            return true;
        } else {
            return false;
        }
    },
    async getCancelReasons() {
        let res = await axios.get(`${baseURL}schedule/boarding/cancel-reason`);
        if (res.data.response && res.data.response.response.data) {
            return res.data.response.response.data;
        } else {
            return [];
        }
    },
    async cancelReservation(data) {
        let d = new Date();
        let cancelDate = Math.floor(d.getTime() / 1000);

        let res = await axios.delete(`${baseURL}schedule/boarding/${data.id}`, {
            params: {
                cancellation_type_id: data.type,
                cancellation_note: data.notes,
                cancellation_date: cancelDate
            }
        });

        if (res.data.messages && res.data.messages["error-on-save"]) {
            throw new Error("Error");
        }
    },
    async extendStay(data) {
        let res = await axios.post(
            `${baseURL}schedule/boarding/extend-stay/${data.id}`,
            {
                end: data.end
            }
        );

        let response = res.data.response;

        if (response.message && response.message.error === 1) {
            throw new Error(response.message.message);
        }

        return true;
    },
    async preCheckOut(data) {
        let url = `${baseURL}schedule/boarding/pre-check-out/${data.id}`;
        let res = await axios.post(url, {
            check_out_datetime: data.datetime, // unix timestamp
            note: data.note
        });

        return res.data;
    },
    async checkOut(data) {
        let url = `${baseURL}schedule/boarding/check-out/${data.id}`;
        let res = await axios.post(url, {
            check_out_datetime: data.datetime, // unix timestamp
            note: data.note
        });

        let response = res.data.response;

        if (response && response.code !== 200) {
            let errMessage = "Error: Unable to checkout";

            if (response.message && response.message.message) {
                errMessage = response.message.message;
            }

            throw new Error(errMessage);
        }

        return true;
    },
    async checkIsAddOnEnabled() {
        let res = await axios.get(`${baseURL}account/extension`);
        let extensions = res.data.response?.resources ?? [];

        const hasBoardingExtension = extensions.some(
            extension =>
                extension.extension_code === "BOARDING" &&
                extension.status === "ENABLED"
        );

        return hasBoardingExtension ? true : false;
    },
    async isNewBoardingEnabled() {
        let res = await axios.get(
            `${baseURL}account/is-feature-enabled?key=boarding`
        );
        if (res.data?.response?.resource?.is_enabled === 1) {
            return true;
        } else {
            return false;
        }
    },
    async toggleNewBoarding(value) {
        await axios.post(`${baseURL}account/enable-disable-feature`, {
            key: "boarding",
            enable: value ? 1 : 0
        });
    }
};
